/**
 * Global state (Zustand).
 *
 * Sprint 2 extension: chiavi X25519 (box), settings privacy, premium, chat state.
 */

import { create } from 'zustand';

export const useStore = create((set, get) => ({
  pendingPin: null,
  setPendingPin: (p) => set({ pendingPin: p }),

  // ── Identity (Sprint 1)
  publicKey: null,       // Ed25519 pubkey (Uint8Array)
  secretKey: null,       // Ed25519 seckey
  uid: null,             // uid derivato (Nullum)
  mnemonic: null,        // 24 parole
  firebaseUser: null,    // user Firebase Anonymous
  sigil: null,

  // ── Identity Sprint 2: X25519 box
  boxPub: null,          // Uint8Array
  boxSec: null,          // Uint8Array

  // ── Settings privacy (mirror locale di /users/{uid})
  findable: true,
  acceptMode: 'request', // 'request' | 'whitelist'
  stealth: false,
  securityLevel: 0,
  showAccountSecurityLevel: false,

  // ── Premium status
  isFounder: false,
  founderNumber: null,
  premiumLifetime: false,
  premium: false,
  // ── Ciclo di vita abbonamento (da getMyAccount)
  premiumStatus: 'none',        // none | lifetime | active | grace | expired
  premiumExpiresAt: null,       // ms epoch della scadenza (null = a vita)
  premiumDaysLeft: null,        // giorni alla scadenza (status active)
  premiumGraceDaysLeft: null,   // giorni residui di grazia (status grace)
  betaTester: false,            // se true: non può creare inviti (controllo beta)

  // ── Sigil derivati (premium): { sigilLower → { boxPub, boxSec, signPub, signSec, sigil } }
  // Derivati al volo dalla mnemonica al login + dopo creazione di un nuovo derivato.
  derivedKeys: {},

  // ── Setters
  setIdentity: ({ publicKey, secretKey, uid, mnemonic }) =>
    set({ publicKey, secretKey, uid, mnemonic }),

  setBoxKeys: ({ boxPub, boxSec }) => set({ boxPub, boxSec }),

  setPremium: (premium) => set({ premium: !!premium }),

  // Stato completo del ciclo di vita abbonamento (dopo getMyAccount).
  setPremiumInfo: (info) => set({
    premium: !!info?.premium,
    premiumStatus: info?.premiumStatus || 'none',
    premiumExpiresAt: info?.premiumExpiresAt ?? null,
    premiumDaysLeft: info?.premiumDaysLeft ?? null,
    premiumGraceDaysLeft: info?.premiumGraceDaysLeft ?? null,
    betaTester: !!info?.betaTester,
  }),

  // Sostituisce tutto l'elenco di chiavi derivate (usato dopo getMyAccount).
  setDerivedKeys: (map) => set({ derivedKeys: map || {} }),

  // Aggiunge una entry (usato dopo createDerivedSigil).
  addDerivedKeys: (sigilLower, entry) => set((s) => ({
    derivedKeys: { ...s.derivedKeys, [sigilLower]: entry },
  })),

  removeDerivedKeys: (sigilLower) => set((s) => {
    const next = { ...s.derivedKeys };
    delete next[sigilLower];
    return { derivedKeys: next };
  }),

  setFirebaseUser: (user) => set({ firebaseUser: user }),

  setSigil: (sigil) => set({ sigil }),

  setProfile: (profile) => set({
    sigil: profile.sigil ?? get().sigil,
    findable: profile.findable ?? true,
    acceptMode: profile.acceptMode ?? 'request',
    stealth: profile.stealth ?? false,
    securityLevel: profile.securityLevel ?? 0,
    showAccountSecurityLevel: profile.showAccountSecurityLevel ?? false,
    isFounder: profile.isFounder ?? false,
    founderNumber: profile.founderNumber ?? null,
    premiumLifetime: profile.premiumLifetime ?? false,
  }),

  reset: () => set({
    publicKey: null, secretKey: null, uid: null, mnemonic: null,
    firebaseUser: null, sigil: null, boxPub: null, boxSec: null,
    findable: true, acceptMode: 'request', stealth: false,
    securityLevel: 0, showAccountSecurityLevel: false,
    isFounder: false, founderNumber: null, premiumLifetime: false,
    premium: false, premiumStatus: 'none', premiumExpiresAt: null,
    premiumDaysLeft: null, premiumGraceDaysLeft: null, betaTester: false, derivedKeys: {},
  }),

  isAuthenticated: () => {
    const s = get();
    return !!(s.uid && s.sigil);
  },
}));
